import React, { useEffect, useRef, useState } from "react";
import { Play, Square, Check, Volume2, ShieldCheck, Flame, ChevronRight, RefreshCw, Zap, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { createAudioContext } from "../lib/audio-utils";

interface VocalWarmupProps {
  pitchProfile: "low-male" | "medium-male" | "female-high";
  selectedLang?: string;
}

const WARMUP_LOCALIZED: Record<string, any> = {
  en: {
    title: "Vedic Vocal Warmup (Svara Purvanga)",
    subtitle: "Prepare your vocal cords and adjust resonance alignment before chanting classical mantras.",
    startBtn: "Start Listening",
    stopBtn: "Stop",
    listenBtn: "Play Reference Guide",
    holdSuccess: "Held successfully!",
    completedAll: "Vocal Warmup Complete! You are fully prepared for Vedic recitation.",
    profileLabel: "Voice Profile",
    currentPitch: "Your Pitch",
    targetRange: "Target Range",
    statusListening: "Adjust your vocal pitch to match the highlighted range...",
    statusSuccess: "Splendid resonance! Proceed to the next exercise.",
    statusIdle: "Click 'Start Listening' to begin tuning your voice.",
    exerciseTitle: "Warmup Steps",
    secLeft: "seconds left"
  },
  hi: {
    title: "वैदिक स्वर पूर्वार्ध (स्वर-अभ्यास)",
    subtitle: "मंत्रोच्चारण आरम्भ करने से पूर्व कण्ठ स्वर तथा नाद तरंगों को सन्तुलित करें।",
    startBtn: "ध्वनि ग्रहण आरम्भ करें",
    stopBtn: "विराम लें",
    listenBtn: "सन्दर्भ ध्वनि सुनें",
    holdSuccess: "सफलतापूर्वक सन्तुलित!",
    completedAll: "स्वर अभ्यास पूर्ण! अब आप शुद्ध वैदिक पाठ के लिए पूर्णतः तत्पर हैं।",
    profileLabel: "स्वर प्रकार",
    currentPitch: "आपका स्वर",
    targetRange: "लक्ष्य सीमा",
    statusListening: "अपने कंठ स्वर को हाइलाइट किए गए स्वर क्षेत्र से मिलाएँ...",
    statusSuccess: "उत्कृष्ट स्वर साधना! अगले अभ्यास पर बढ़ें।",
    statusIdle: "स्वर ट्यूनिंग शुरू करने के लिए 'ध्वनि ग्रहण आरम्भ करें' दबाएं।",
    exerciseTitle: "अभ्यास चरण",
    secLeft: "सेकंड शेष"
  },
  ne: {
    title: "वैदिक स्वर पूर्वार्ङ्ग (स्वर-अभ्यास)",
    subtitle: "मन्त्रोच्चारण सुरु गर्नु अघि कण्ठ स्वर र नाद तरङ्गहरूलाई सन्तुलित गर्नुहोस्।",
    startBtn: "ध्वनि ग्रहण सुरु गर्नुहोस्",
    stopBtn: "विराम",
    listenBtn: "सन्दर्भ ध्वनि सुन्नुहोस्",
    holdSuccess: "सफलतापूर्वक सन्तुलित!",
    completedAll: "स्वर अभ्यास पूर्ण! अब तपाईं शुद्ध वैदिक पाठका लागि पूर्ण रूपमा तयार हुनुहुन्छ।",
    profileLabel: "स्वरको किसिम",
    currentPitch: "तपाईंको स्वर",
    targetRange: "लक्ष्य सीमा",
    statusListening: "आफ्नो कण्ठ स्वरलाई हाइलाइट गरिएको स्वर क्षेत्रसँग मिलाउनुहोस्...",
    statusSuccess: "उत्कृष्ट स्वर साधना! अर्को अभ्यासमा जानुहोस्।",
    statusIdle: "स्वर ट्यूनिङ सुरु गर्न 'ध्वनि ग्रहण सुरु गर्नुहोस्' मा क्लिक गर्नुहोस्।",
    exerciseTitle: "अभ्यासका चरणहरू",
    secLeft: "सेकेन्ड बाँकी"
  },
  sa: {
    title: "वैदिकस्वरपूर्वाङ्गम् (स्वरसाधनम्)",
    subtitle: "मन्त्रोच्चारणात् पूर्वं कण्ठस्वरस्य नादतरङ्गाणां च शुद्धीकरणं कुरुत।",
    startBtn: "ध्वनिग्रहणं कुरुत",
    stopBtn: "विरामः",
    listenBtn: "सन्दर्भध्वनिं शृणोतु",
    holdSuccess: "शुद्धं सामञ्जस्यम्!",
    completedAll: "स्वरपूर्वाङ्गं समाप्तम्! अधुना भवन्तः शुद्धवैदिकमन्त्रपाठाय सज्जाः सन्ति।",
    profileLabel: "कण्ठस्वरप्रकारः",
    currentPitch: "भवतः स्वरः",
    targetRange: "लक्ष्यसीमा",
    statusListening: "उच्चारितस्वरं निर्दिष्टलक्ष्यसीम्ना सह मेलयतु...",
    statusSuccess: "अतीव सुन्दरम्! अग्रिमं सोपानं गच्छतु।",
    statusIdle: "स्वरसाधनां कर्तुं 'ध्वनिग्रहणं कुरुत' नुदतु।",
    exerciseTitle: "साधनासोपानानि",
    secLeft: "क्षणानि शिष्टानि"
  }
};

interface Exercise {
  id: string;
  name: string;
  sanskritName: string;
  description: string;
  targetSwara: "Anudatta" | "Svarita" | "Udatta";
  requiredHoldSeconds: number;
}

export default function VocalWarmup({ pitchProfile, selectedLang = "en" }: VocalWarmupProps) {
  const t = WARMUP_LOCALIZED[selectedLang] || WARMUP_LOCALIZED.en;

  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [currentPitch, setCurrentPitch] = useState<number | null>(null);
  const [currentVolume, setCurrentVolume] = useState<number>(0);
  const [holdTimer, setHoldTimer] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [warmupStreak, setWarmupStreak] = useState<number>(0);
  const [feedbackMsg, setFeedbackMsg] = useState<string>(t.statusIdle);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<any>(null);
  const audioContextForGuideRef = useRef<AudioContext | null>(null);

  // Buffer caching to prevent GC overhead
  const audioDataBufferRef = useRef<Float32Array | null>(null);
  const lastStateUpdateRef = useRef<number>(0);
  const latestPitchRef = useRef<number | null>(null);
  const latestVolumeRef = useRef<number>(0);

  // AudioContext Battery Efficiency & Auto-Suspension on Tab Concealment
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (audioContextRef.current && audioContextRef.current.state === 'running') {
          audioContextRef.current.suspend();
        }
        if (audioContextForGuideRef.current && audioContextForGuideRef.current.state === 'running') {
          audioContextForGuideRef.current.suspend();
        }
      } else {
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
        if (audioContextForGuideRef.current && audioContextForGuideRef.current.state === 'suspended') {
          audioContextForGuideRef.current.resume();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const profileOffsets = {
    "low-male": { low: 85, mid: 115, high: 145 },
    "medium-male": { low: 110, mid: 145, high: 180 },
    "female-high": { low: 165, mid: 210, high: 260 }
  };

  const zones = profileOffsets[pitchProfile];

  const exercises: Exercise[] = [
    {
      id: "grounding_hum",
      name: "Grounding Pranava Hum",
      sanskritName: "प्रणव नाद (Anudatta)",
      description: "Recite a deep, humming sound from your lower abdomen (OM) matching the low pitch accent range.",
      targetSwara: "Anudatta",
      requiredHoldSeconds: 3
    },
    {
      id: "steady_madhyama",
      name: "Steady Sthira Hold",
      sanskritName: "मध्यम स्थिर स्वर (Svarita)",
      description: "Recite a balanced central tone to open up your throat muscles stably in the medium pitch range.",
      targetSwara: "Svarita",
      requiredHoldSeconds: 3
    },
    {
      id: "high_sky_resonance",
      name: "Urdhva Taranga Accent",
      sanskritName: "उच्च शिखर नाद (Udatta)",
      description: "Produce a high, clear vowel resonance from the top palette to engage the high pitch Vedic accent.",
      targetSwara: "Udatta",
      requiredHoldSeconds: 3
    }
  ];

  const currentExercise = exercises[activeStepIdx] || exercises[0];

  // Fetch local warm-up stats on mount
  useEffect(() => {
    const savedStreak = localStorage.getItem("vedic_vocal_warmup_streak");
    if (savedStreak) {
      setWarmupStreak(parseInt(savedStreak, 10));
    }
    const savedCompleted = localStorage.getItem("vedic_vocal_warmup_completed_ids");
    if (savedCompleted) {
      try {
        setCompletedSteps(JSON.parse(savedCompleted));
      } catch (e) {}
    }
  }, []);

  const getTargetFrequencyRange = (swara: "Anudatta" | "Svarita" | "Udatta") => {
    switch (swara) {
      case "Anudatta":
        return { min: 70, max: zones.low };
      case "Svarita":
        return { min: zones.low, max: zones.high };
      case "Udatta":
        return { min: zones.high, max: 500 };
    }
  };

  const targetRange = getTargetFrequencyRange(currentExercise.targetSwara);

  // Pitch-matching checker loop called 60 FPS
  const checkPitchAlignment = (hz: number) => {
    if (hz >= targetRange.min && hz <= targetRange.max) {
      return true;
    }
    return false;
  };

  // Play synthetic pure-tone oscillator guide
  const playGuideReference = () => {
    try {
      if (audioContextForGuideRef.current) {
        audioContextForGuideRef.current.close();
      }

      const ctx = createAudioContext();
      if (!ctx) return;
      audioContextForGuideRef.current = ctx;

      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();

      // Set frequency halfway through the range
      const midHz = (targetRange.min + (targetRange.max === 500 ? targetRange.min * 1.3 : targetRange.max)) / 2;
      osc.type = "sine";
      osc.frequency.setValueAtTime(midHz, ctx.currentTime);

      gainNode.gain.setValueAtTime(0.001, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.8);

      osc.connect(gainNode);
      gainNode.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 1.8);
    } catch (e) {
      console.error("Oscillator playback failed:", e);
    }
  };

  const startTuning = async () => {
    try {
      if (audioContextForGuideRef.current) {
        audioContextForGuideRef.current.close();
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioCtx = createAudioContext();
      if (!audioCtx) return;
      audioContextRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      setIsListening(true);
      setFeedbackMsg(t.statusListening);
      setHoldTimer(0);

      // Reset performance optimization values
      lastStateUpdateRef.current = 0;
      latestPitchRef.current = null;
      latestVolumeRef.current = 0;

      processWarmupAudio();
    } catch (err) {
      console.error("Vocal Warmup Microphone error:", err);
      setFeedbackMsg("Microphone access denied. Please allow permission to tune voice.");
    }
  };

  const stopTuning = () => {
    setIsListening(false);
    setCurrentPitch(null);
    setCurrentVolume(0);
    setHoldTimer(0);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  };

  // Autocorrelation Pitch-Detection Algorithm
  const detectPitch = (buffer: Float32Array, sampleRate: number): number => {
    const SIZE = buffer.length;
    let sum = 0;
    for (let i = 0; i < SIZE; i++) {
      sum += buffer[i] * buffer[i];
    }
    const rms = Math.sqrt(sum / SIZE);
    latestVolumeRef.current = rms;

    if (rms < 0.015) {
      return -1;
    }

    let r1 = 0;
    let r2 = SIZE - 1;
    const threshold = 0.15;
    for (let i = 0; i < SIZE / 2; i++) {
      if (Math.abs(buffer[i]) < threshold) {
        r1 = i;
        break;
      }
    }
    for (let i = SIZE - 1; i >= SIZE / 2; i--) {
      if (Math.abs(buffer[i]) < threshold) {
        r2 = i;
        break;
      }
    }

    const slice = buffer.slice(r1, r2);
    const sliceLen = slice.length;
    if (sliceLen < 64) return -1;

    const correlation = new Float32Array(sliceLen);
    for (let i = 0; i < sliceLen; i++) {
      for (let j = 0; j < sliceLen - i; j++) {
        correlation[i] += slice[j] * slice[j + i];
      }
    }

    let zeroCross = 0;
    while (zeroCross < sliceLen - 1 && correlation[zeroCross] > 0) {
      zeroCross++;
    }

    let peakVal = -1;
    let peakIdx = -1;
    for (let i = zeroCross; i < sliceLen; i++) {
      if (correlation[i] > peakVal) {
        peakVal = correlation[i];
        peakIdx = i;
      }
    }

    if (peakIdx !== -1 && correlation[peakIdx] > 0.1) {
      const freq = sampleRate / peakIdx;
      if (freq >= 70 && freq <= 600) {
        return freq;
      }
    }
    return -1;
  };

  // Processing loop
  const processWarmupAudio = () => {
    if (!analyserRef.current || !audioContextRef.current) return;

    const bufferLength = analyserRef.current.fftSize;
    if (!audioDataBufferRef.current || audioDataBufferRef.current.length !== bufferLength) {
      audioDataBufferRef.current = new Float32Array(bufferLength);
    }
    const dataArray = audioDataBufferRef.current;
    analyserRef.current.getFloatTimeDomainData(dataArray);

    const hz = detectPitch(dataArray, audioContextRef.current.sampleRate);
    const now = Date.now();
    const shouldUpdateState = now - lastStateUpdateRef.current > 100; // 10 FPS React state throttler

    if (hz > 0) {
      latestPitchRef.current = Math.round(hz);
    } else {
      latestPitchRef.current = null;
    }

    // Direct performance evaluation on Ref data to keep timing precision
    const isMatched = latestPitchRef.current ? checkPitchAlignment(latestPitchRef.current) : false;

    if (shouldUpdateState) {
      setCurrentPitch(latestPitchRef.current);
      setCurrentVolume(latestVolumeRef.current);
      lastStateUpdateRef.current = now;

      if (isMatched) {
        setHoldTimer((prev) => {
          const nextVal = prev + 0.1;
          if (nextVal >= currentExercise.requiredHoldSeconds) {
            // Success Trigger!
            handleExerciseSuccess();
            return 0;
          }
          return nextVal;
        });
        setFeedbackMsg(`Matching! Hold steady...`);
      } else {
        setHoldTimer((prev) => Math.max(0, prev - 0.05)); // slow decay when off pitch
        if (latestPitchRef.current) {
          setFeedbackMsg(latestPitchRef.current < targetRange.min ? "Slide pitch HIGHER ↑" : "Slide pitch LOWER ↓");
        } else {
          setFeedbackMsg(t.statusListening);
        }
      }
    }

    animationFrameRef.current = requestAnimationFrame(processWarmupAudio);
  };

  const handleExerciseSuccess = () => {
    stopTuning();
    setFeedbackMsg(t.statusSuccess);

    const updated = [...completedSteps];
    if (!updated.includes(currentExercise.id)) {
      updated.push(currentExercise.id);
      setCompletedSteps(updated);
      localStorage.setItem("vedic_vocal_warmup_completed_ids", JSON.stringify(updated));
    }

    // If more exercises, suggest next. If completed all, increase streak!
    if (activeStepIdx < exercises.length - 1) {
      setTimeout(() => {
        setActiveStepIdx((prev) => prev + 1);
        setFeedbackMsg(t.statusIdle);
      }, 2000);
    } else {
      // Completed all warmups! Increment streak
      const newStreak = warmupStreak + 1;
      setWarmupStreak(newStreak);
      localStorage.setItem("vedic_vocal_warmup_streak", newStreak.toString());
      setFeedbackMsg(t.completedAll);
      try { window.dispatchVedicEvent?.("vedic-steps-updated"); } catch(e){}
    }
  };

  const restartWarmup = () => {
    stopTuning();
    setActiveStepIdx(0);
    setCompletedSteps([]);
    localStorage.removeItem("vedic_vocal_warmup_completed_ids");
    setFeedbackMsg(t.statusIdle);
  };

  const getSwarasColor = (swara: "Anudatta" | "Svarita" | "Udatta") => {
    switch (swara) {
      case "Anudatta":
        return "text-blue-400 bg-blue-500/10 border-blue-500/20";
      case "Svarita":
        return "text-yellow-400 bg-yellow-500/10 border-yellow-500/20";
      case "Udatta":
        return "text-orange-400 bg-orange-500/10 border-orange-500/20";
    }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 shadow-xl" id="vocal-warmup-container">
      {/* Warmup Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-neutral-800 pb-4 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-500 animate-pulse" />
            <h3 className="text-sm font-bold text-neutral-100 uppercase tracking-wider font-mono">
              {t.title}
            </h3>
          </div>
          <p className="text-xs text-neutral-500 mt-1">
            {t.subtitle}
          </p>
        </div>

        {/* Practice Streak Badge */}
        {warmupStreak > 0 && (
          <div className="flex items-center gap-1.5 self-start sm:self-auto bg-orange-500/10 border border-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-[11px] font-mono font-bold">
            <Zap className="w-3.5 h-3.5 fill-orange-500/20" />
            <span>Warmup Streak: {warmupStreak} Days</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Exercises Steps Menu */}
        <div className="lg:col-span-5 flex flex-col gap-2.5">
          <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest block mb-1">
            {t.exerciseTitle}
          </span>
          {exercises.map((ex, idx) => {
            const isCompleted = completedSteps.includes(ex.id);
            const isActive = idx === activeStepIdx;
            const swaraColorClasses = getSwarasColor(ex.targetSwara);

            return (
              <button
                key={ex.id}
                onClick={() => {
                  stopTuning();
                  setActiveStepIdx(idx);
                  setFeedbackMsg(t.statusIdle);
                }}
                className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-center justify-between cursor-pointer ${
                  isActive
                    ? "bg-neutral-950 border-amber-500/40 shadow-inner"
                    : isCompleted
                    ? "bg-neutral-950/40 border-emerald-950/20 hover:bg-neutral-950 hover:border-neutral-800"
                    : "bg-neutral-950/20 border-neutral-850 hover:bg-neutral-950 hover:border-neutral-800"
                }`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-mono font-bold ${
                    isCompleted
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                      : isActive
                      ? "bg-amber-500 text-neutral-950"
                      : "bg-neutral-800 text-neutral-500"
                  }`}>
                    {isCompleted ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-neutral-200 truncate flex items-center gap-1.5">
                      <span>{ex.name}</span>
                    </div>
                    <span className="text-[10px] text-neutral-500 block mt-0.5">{ex.sanskritName}</span>
                  </div>
                </div>

                {/* Svara Pill Indicator */}
                <div className={`text-[9px] font-mono px-2 py-0.5 rounded border ${swaraColorClasses}`}>
                  {ex.targetSwara}
                </div>
              </button>
            );
          })}

          <button
            onClick={restartWarmup}
            className="mt-2 text-[10px] font-mono font-semibold text-neutral-500 hover:text-neutral-300 flex items-center gap-1.5 self-start px-2 py-1 hover:bg-neutral-800/40 rounded transition cursor-pointer"
          >
            <RefreshCw className="w-3 h-3" />
            Reset Warmup Sequence
          </button>
        </div>

        {/* Live Active Tuning Arena */}
        <div className="lg:col-span-7 bg-neutral-950/50 border border-neutral-800/40 rounded-xl p-5 flex flex-col justify-between">
          <div className="flex items-start justify-between border-b border-neutral-900 pb-3 mb-4">
            <div>
              <span className="text-[9px] font-bold text-neutral-500 uppercase tracking-widest block">
                Active Tuning Guide
              </span>
              <h4 className="text-xs font-bold text-neutral-200 mt-0.5">
                {currentExercise.name} • <span className="text-amber-500 font-mono">{currentExercise.sanskritName}</span>
              </h4>
            </div>
            {/* Range info badge */}
            <div className="text-[10px] font-mono text-neutral-400 bg-neutral-900 border border-neutral-800 px-2.5 py-1 rounded">
              Target Range: <span className="text-amber-400 font-bold">{targetRange.min} - {targetRange.max === 500 ? "∞" : targetRange.max} Hz</span>
            </div>
          </div>

          <p className="text-xs text-neutral-400 leading-relaxed mb-4">
            {currentExercise.description}
          </p>

          {/* Interactive Visualizer Node */}
          <div className="bg-neutral-950 border border-neutral-900 rounded-xl p-4 flex flex-col items-center justify-center min-h-[140px] relative overflow-hidden mb-4">
            
            {/* Hold progress timer overlay */}
            {isListening && holdTimer > 0 && (
              <div className="absolute inset-0 bg-amber-500/5 animate-pulse flex items-center justify-center pointer-events-none" />
            )}

            {/* Current Pitch Dial/Value Display */}
            <div className="z-10 text-center flex flex-col items-center">
              {currentPitch ? (
                <div className="flex flex-col items-center">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-3xl font-black text-amber-500 font-mono tracking-tight flex items-baseline gap-1"
                  >
                    {currentPitch}
                    <span className="text-sm font-medium text-neutral-500">Hz</span>
                  </motion.div>
                  <span className="text-[10px] font-mono uppercase font-bold text-amber-400/80 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 mt-1">
                    Voice Active
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center py-2">
                  <Volume2 className="w-8 h-8 text-neutral-700 mb-1" />
                  <span className="text-xs font-mono text-neutral-600">Silence Detected</span>
                </div>
              )}
            </div>

            {/* Micro gauge/bar demonstrating pitch positioning */}
            <div className="w-full mt-5 px-3">
              <div className="flex items-center justify-between text-[8px] font-mono text-neutral-600 mb-1">
                <span>70 Hz (Low)</span>
                <span>{zones.low} Hz</span>
                <span>{zones.high} Hz</span>
                <span>500 Hz (High)</span>
              </div>
              
              {/* Scale track bar with colored zones */}
              <div className="h-2.5 w-full bg-neutral-900 rounded-full flex overflow-hidden relative border border-neutral-800">
                <div className="h-full bg-blue-500/20 border-r border-neutral-800" style={{ width: `${((zones.low - 70) / 430) * 100}%` }} />
                <div className="h-full bg-yellow-500/20 border-r border-neutral-800" style={{ width: `${((zones.high - zones.low) / 430) * 100}%` }} />
                <div className="h-full bg-orange-500/20" style={{ width: `${((500 - zones.high) / 430) * 100}%` }} />

                {/* Animated pointer cursor showing live pitch */}
                {currentPitch && (
                  <motion.div
                    className="absolute top-0 bottom-0 w-1 bg-amber-500 shadow-lg shadow-amber-500/50"
                    animate={{ left: `${Math.min(100, Math.max(0, ((currentPitch - 70) / 430) * 100))}%` }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  />
                )}
              </div>

              {/* Range highlighter matching current target Swara */}
              <div className="flex justify-center mt-2">
                <span className="text-[9px] font-mono font-medium text-neutral-500 flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    currentExercise.targetSwara === "Anudatta" ? "bg-blue-500" :
                    currentExercise.targetSwara === "Svarita" ? "bg-yellow-500" : "bg-orange-500"
                  }`} />
                  Exercise target: <strong className="text-neutral-300">{currentExercise.targetSwara} Zone</strong>
                </span>
              </div>
            </div>

            {/* Bottom mini-timer track */}
            {isListening && holdTimer > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-neutral-900">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-100"
                  style={{ width: `${(holdTimer / currentExercise.requiredHoldSeconds) * 100}%` }}
                />
              </div>
            )}
          </div>

          {/* Action Controllers */}
          <div className="flex flex-col sm:flex-row gap-3">
            {isListening ? (
              <button
                onClick={stopTuning}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center justify-center gap-1.5 transition cursor-pointer"
              >
                <Square className="w-3.5 h-3.5 fill-current" />
                {t.stopBtn}
              </button>
            ) : (
              <button
                onClick={startTuning}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-neutral-950 text-xs font-black py-2.5 px-4 rounded-xl flex items-center justify-center gap-1.5 transition cursor-pointer"
              >
                <Play className="w-3.5 h-3.5 fill-current" />
                {t.startBtn}
              </button>
            )}

            <button
              onClick={playGuideReference}
              className="px-4 py-2.5 rounded-xl border border-neutral-800 text-neutral-300 hover:text-neutral-100 hover:bg-neutral-800/40 transition text-xs font-semibold flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Volume2 className="w-3.5 h-3.5" />
              {t.listenBtn}
            </button>
          </div>

          {/* Prompt/Guide Message Banner */}
          <div className="mt-4 bg-neutral-950 border border-neutral-900 rounded-lg p-3 text-[11px] text-neutral-400 flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0 animate-pulse" />
            <div className="flex-1">
              {isListening && holdTimer > 0 ? (
                <div className="flex items-center justify-between">
                  <span className="text-amber-400 font-bold">Steady resonance detected!</span>
                  <span className="font-mono text-neutral-500 text-[10px]">{(currentExercise.requiredHoldSeconds - holdTimer).toFixed(1)} {t.secLeft}</span>
                </div>
              ) : (
                <span>{feedbackMsg}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
